import { FLOW_STEP_PATHS } from '@/constants/paths';
import SignupProductSelection from '@/containers/SignupProductSelection';
import { ContactDetails } from '@/containers/ContactDetails';
import { BasicInfo } from '@/containers/BasicInfo';
import { ContactReceived } from '@/containers/ContactReceived';
import { WeAreSorry } from '@/containers/WeAreSorry';
import { Platform, PlatformOther } from '@/containers/Platform';
import NativePlatform from '@/containers/NativePlatform';
import { AccountCreationLoading } from '@/containers/AccountCreationLoading';
import { UserAlreadyExists } from '@/containers/UserAlreadyExists';
import { SensitiveGoods } from '@/containers/SensitiveGoods';
import { SignupGateway } from '@/containers/SignupGateway';
import { VolumeSeparation } from '@/containers/VolumeSeparation';
import { not, or } from '@/flow/common';
import {
  hasManualContactPlatform,
  hasManualRedirectCountry,
  hasNativePlatform,
  hasNoPlatforms,
  hasOtherPlatform,
  hasStandardPlatform,
  hasSupportedCountryAndPlatform,
  hasSupportedPlatform,
} from '@/selectors/conditions';

import {
  isExistingUser,
  hasAboveThresholdVolume,
  sellsSensitiveGoods,
  shouldGoThroughAutomaticOnboarding,
} from '@/selectors/data';

const flow = {
  selectors: {
    hasAboveThresholdVolume,
    hasManualContactPlatform,
    hasManualRedirectCountry,
    hasNativePlatform,
    hasNoPlatforms,
    hasOtherPlatform,
    hasStandardPlatform,
    hasSupportedCountryAndPlatform,
    hasSupportedPlatform,
    isExistingUser,
    sellsSensitiveGoods,
    shouldGoThroughAutomaticOnboarding,
  },
  steps: [
    {
      id: 'signup-gateway',
      path: FLOW_STEP_PATHS.SIGNUP_GATEWAY,
      Container: SignupGateway,
      back: false,
      next: FLOW_STEP_PATHS.BASIC_INFO,
    },
    {
      id: 'basic-info',
      path: FLOW_STEP_PATHS.BASIC_INFO,
      Container: BasicInfo,
      back: false,
      next: [
        {
          path: FLOW_STEP_PATHS.WE_ARE_SORRY,
          condition: 'hasNoPlatforms',
        },
        { path: FLOW_STEP_PATHS.VOLUME_SEPARATION, condition: true },
      ],
    },
    {
      id: 'volume-separation',
      path: FLOW_STEP_PATHS.VOLUME_SEPARATION,
      Container: VolumeSeparation,
      back: FLOW_STEP_PATHS.BASIC_INFO,
      next: FLOW_STEP_PATHS.SENSITIVE_GOODS,
    },
    {
      id: 'sensitive-goods',
      path: FLOW_STEP_PATHS.SENSITIVE_GOODS,
      Container: SensitiveGoods,
      back: FLOW_STEP_PATHS.VOLUME_SEPARATION,
      next: [
        {
          path: FLOW_STEP_PATHS.WE_ARE_SORRY,
          condition: or(
            ['sellsSensitiveGoods', not('hasAboveThresholdVolume')],
            [
              'sellsSensitiveGoods',
              'hasAboveThresholdVolume',
              'shouldGoThroughAutomaticOnboarding',
            ],
          ),
        },
        {
          path: FLOW_STEP_PATHS.PLATFORM,
          condition: true,
        },
      ],
    },
    {
      id: 'platform',
      path: FLOW_STEP_PATHS.PLATFORM,
      Container: Platform,
      back: FLOW_STEP_PATHS.SENSITIVE_GOODS,
      next: [
        {
          path: FLOW_STEP_PATHS.PLATFORM_NATIVE,
          condition: 'hasNativePlatform',
        },
        {
          path: FLOW_STEP_PATHS.PLATFORM_OTHER,
          condition: 'hasOtherPlatform',
        },
        {
          path: FLOW_STEP_PATHS.CONTACT_DETAILS,
          condition: or(
            ['hasManualRedirectCountry', 'hasStandardPlatform'],
            not('shouldGoThroughAutomaticOnboarding'),
          ),
        },
        {
          path: FLOW_STEP_PATHS.PRODUCT_SELECTION_SIGNUP_PATH,
          condition: 'hasStandardPlatform',
        },
      ],
    },
    {
      id: 'platform-other',
      path: FLOW_STEP_PATHS.PLATFORM_OTHER,
      Container: PlatformOther,
      prerequisite: 'hasOtherPlatform',
      back: FLOW_STEP_PATHS.PLATFORM,
      next: [
        {
          path: FLOW_STEP_PATHS.PLATFORM_NATIVE,
          condition: 'hasNativePlatform',
        },
        {
          path: FLOW_STEP_PATHS.CONTACT_DETAILS,
          condition: or(
            'hasManualRedirectCountry',
            not('shouldGoThroughAutomaticOnboarding'),
            'hasManualContactPlatform',
          ),
        },
        {
          path: FLOW_STEP_PATHS.PRODUCT_SELECTION_SIGNUP_PATH,
          condition: 'hasSupportedPlatform',
        },
        {
          path: FLOW_STEP_PATHS.WE_ARE_SORRY,
          condition: true,
        },
      ],
    },
    {
      id: 'platform-native',
      path: FLOW_STEP_PATHS.PLATFORM_NATIVE,
      prerequisite: 'hasNativePlatform',
      Container: NativePlatform,
      back: FLOW_STEP_PATHS.PLATFORM,
      next: false,
    },
    {
      id: 'products-select',
      path: FLOW_STEP_PATHS.PRODUCT_SELECTION_SIGNUP_PATH,
      prerequisite: 'hasSupportedCountryAndPlatform',
      Container: SignupProductSelection,
      back: [
        {
          path: FLOW_STEP_PATHS.PLATFORM,
          condition: 'hasStandardPlatform',
        },
        {
          path: FLOW_STEP_PATHS.PLATFORM_OTHER,
          condition: true,
        },
      ],
      next: FLOW_STEP_PATHS.ACCOUNT_CREATION_LOADING,
    },
    {
      id: 'contact-details',
      path: FLOW_STEP_PATHS.CONTACT_DETAILS,
      prerequisite: not('shouldGoThroughAutomaticOnboarding'),
      Container: ContactDetails,
      back: [
        {
          path: FLOW_STEP_PATHS.PLATFORM,
          condition: 'hasStandardPlatform',
        },
        {
          path: FLOW_STEP_PATHS.PLATFORM_OTHER,
          condition: true,
        },
      ],
      next: FLOW_STEP_PATHS.CONTACT_RECEIVED,
    },
    {
      id: 'contact-received',
      path: FLOW_STEP_PATHS.CONTACT_RECEIVED,
      prerequisite: not('shouldGoThroughAutomaticOnboarding'),
      Container: ContactReceived,
      back: FLOW_STEP_PATHS.CONTACT_DETAILS,
      next: false,
    },
    {
      id: 'we-are-sorry',
      path: FLOW_STEP_PATHS.WE_ARE_SORRY,
      Container: WeAreSorry,
      back: [
        {
          path: FLOW_STEP_PATHS.BASIC_INFO,
          condition: 'hasNoPlatforms',
        },
        {
          path: FLOW_STEP_PATHS.SENSITIVE_GOODS,
          condition: 'sellsSensitiveGoods',
        },
        {
          path: FLOW_STEP_PATHS.PLATFORM_OTHER,
          condition: true,
        },
      ],
      next: false,
    },
    {
      id: 'account-creation-loading',
      path: FLOW_STEP_PATHS.ACCOUNT_CREATION_LOADING,
      Container: AccountCreationLoading,
      back: FLOW_STEP_PATHS.PRODUCT_SELECTION_SIGNUP_PATH,
      next: [
        {
          path: FLOW_STEP_PATHS.USER_ALREADY_EXISTS,
          condition: 'isExistingUser',
        },
      ],
    },
    {
      id: 'user-already-exists',
      path: FLOW_STEP_PATHS.USER_ALREADY_EXISTS,
      Container: UserAlreadyExists,
      back: false,
      next: false,
    },
  ],
};

export default ({ features = {} } = {}) => flow;
